import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { revalidateLicenseMode } from "../../api/client";
import {
  isMonitorScreenPath,
  type PendingLicenseNotice,
} from "../../lib/licenseNotice";
import {
  onBlockedLicense,
  takeBlockedLicense,
} from "../../lib/licenseBlockedHandoff";
import {
  isLicenseReadOnlyRole,
  onLicenseReadOnlyNotice,
  useLicenseReadOnly,
} from "../../lib/licenseReadOnly";
import {
  dismissLicenseNotice,
  showLicenseBlocked,
  showLicenseReadOnly,
} from "../../store/authSlice";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import LicenseNoticeModal from "./LicenseNoticeModal";

/**
 * Показва предупреждението за лиценза, оставено в Redux от входа
 * (`auth.licenseNotice`). Монтиран веднъж в `main.tsx` до `InstallBanner`, извън
 * route-овете — входът навигира веднага след успеха, а модалът трябва да
 * оцелее навигацията.
 *
 * - Предупреждението чака заредената самоличност (`auth.user`), за да се
 *   появи ВЕДНАГА СЛЕД входа, а не над екрана за вход/ПИН клавиатурата.
 * - Отказът след гратисния период (`blocked`) се показва веднага — човекът
 *   стои на екрана за вход.
 * - Никога на /monitors (кухненските екрани нямат такъв модал); там
 *   предупреждението просто чака.
 * - Redux се чисти чак след анимацията на затваряне (`afterClose`), за да не
 *   изчезне съдържанието, докато модалът още избледнява.
 * - Спрян лиценз на ВЕЧЕ влезлия (403 на коя да е заявка, кадър по WS):
 *   изходът е пълна навигация към /login, затова отказът идва през
 *   `licenseBlockedHandoff` — от sessionStorage при монтиране или веднага,
 *   ако навигация не е имало.
 * - „Само преглед" (`lib/licenseReadOnly.ts`): клик на друг модул, на бутон
 *   за действие или отказан запис — сигналът идва от `onLicenseReadOnlyNotice`.
 *   Всеки такъв сигнал пуска и тиха проверка на режима (`revalidateLicenseMode`)
 *   — подновен лиценз се вижда и на екран без заявки („Модули").
 * - „Разбрах" потвърждава ТОЗИ модал: ново съобщение, дошло по време на
 *   анимацията на затваряне, не се изтрива с него, а се показва.
 * - В режим „само преглед" печатът от браузъра (Ctrl/Cmd+P) е празна страница.
 */
export default function LicenseNoticeHost() {
  const pending = useAppSelector((state) => state.auth.licenseNotice);
  // Самоличността на влезлия ТОЗИ път: на общ терминал `auth.user` е на
  // работната точка, докато служителят още е на ПИН клавиатурата —
  // `loginWithCode` вече е сменил `userId`, а неговото `/user/info` не е дошло.
  const userLoaded = useAppSelector(
    (state) => state.auth.user !== null && state.auth.user.id === state.auth.userId,
  );
  // Брояч, оставен отворен до края на гратиса: със „Справки"/„Склад" човекът
  // остава в режим „само преглед", не е спрян.
  const readOnlyAfterGrace = useAppSelector((state) =>
    state.auth.roles.some((role) => isLicenseReadOnlyRole(role.name)),
  );
  const licenseReadOnly = useLicenseReadOnly();
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  // Модалът, потвърден с „Разбрах" (докато трае анимацията на затваряне).
  const [acknowledgedNotice, setAcknowledgedNotice] =
    useState<PendingLicenseNotice | null>(null);
  const pendingRef = useRef(pending);
  pendingRef.current = pending;

  useEffect(() => {
    const handedOver = takeBlockedLicense();
    if (handedOver) dispatch(showLicenseBlocked(handedOver));
    const stopBlocked = onBlockedLicense((detail) => dispatch(showLicenseBlocked(detail)));
    const stopReadOnly = onLicenseReadOnlyNotice((expiresAt) => {
      dispatch(showLicenseReadOnly(expiresAt));
      revalidateLicenseMode();
    });
    return () => {
      stopBlocked();
      stopReadOnly();
    };
  }, [dispatch]);

  useEffect(() => {
    document.documentElement.classList.toggle("print:hidden", licenseReadOnly !== null);
  }, [licenseReadOnly]);

  if (!pending) return null;

  const visible =
    !isMonitorScreenPath(pathname) && (pending.blocked || userLoaded);

  return (
    <LicenseNoticeModal
      open={visible && pending !== acknowledgedNotice}
      pending={pending}
      readOnlyAfterGrace={readOnlyAfterGrace}
      onAcknowledge={() => setAcknowledgedNotice(pending)}
      afterClose={() => {
        // Скрит заради /monitors не значи „Разбрах" — предупреждението остава;
        // по-ново съобщение (дошло по време на затварянето) също остава.
        if (acknowledgedNotice === null) return;
        setAcknowledgedNotice(null);
        if (pendingRef.current === acknowledgedNotice) dispatch(dismissLicenseNotice());
      }}
    />
  );
}
